// @ts-ignore
import MapboxDraw, {DrawCustomMode, DrawCustomModeThis} from "@mapbox/mapbox-gl-draw";
import {AbstractDrawMode} from "../draw-modes/abstract-draw-mode";
import {MapController} from "../map-controller";
import {Layer, LayerGeometry} from "../../document/model/layer";
import {MapEvent} from "../../document/model/map";
import {Feature, Position} from "geojson";
import {GeometryProvider} from "../providers/geometry-provider";
import {AbstractInteraction, InteractionDefinition, InteractionProxy, InteractionState} from "../interaction/abstract-interaction";
import {MenuItem} from "primevue/menuitem";
// @ts-ignore
import {getDocumentCategoryId, getDocumentTypeId, getModuleId, getRightId, hasAccessTo} from "@/js/user";
import {VueComponentControl} from "../control/VueComponentControl";
import {getFilteredObjectByKeyPrefix} from "../../utils";
import {FormInteractionAction} from "./custom-mode";
import FormRenderPageModalContainer from "../../../pages/documents/FormRenderPageModalContainer.vue";

export class GeometryCoordinate {
  path: string;
  coordinate: Position;
}

export abstract class AbstractInteractionMode {
  static identifier: string;
  static title: string;
  static icon: string;
  static hidden: boolean = false;

  static interactions: {[key: string]: InteractionDefinition} = {};

  static defaultInteraction: string = 'select';

  static requiresLayer: boolean = false;

  static allowChanges: boolean = false;
  static allowPriorityContextMenu: boolean = false;

  static showActionBar: boolean = true;
  static actionBarSize: 'small' | 'large' | undefined = 'small';
  static actionBarShowLabels: boolean = true;

  static drawStyles: any;

  _drawControl: MapboxDraw | undefined = undefined;

  _mapInstance: mapboxgl.Map;

  _interactionMap: {[key: string]: AbstractInteraction<InteractionState>} = {};

  currentInteraction: AbstractInteraction<InteractionState>;
  currentInteractionIdentifier: string | undefined;
  currentInteractionState: InteractionState;
  currentInteractionProxy: (DrawCustomMode & DrawCustomModeThis) | undefined;
  currentInteractionAPI: any;
  _controls: VueComponentControl[] = [];

  public _customButtonSubscriber;

  constructor(
    //@ts-ignore
    protected controller: MapController,
    protected layer: Layer | undefined = undefined,
    protected defaultInteraction: string | undefined = undefined,
    protected drawStyles: any | undefined = undefined
  ) {
    if ((<typeof AbstractInteractionMode>this.constructor).requiresLayer && layer === undefined) {
      throw new Error(`Map mode ${this.getIdentifier()} requires a layer`);
    }

    this.controller.mapInstance.once(MapEvent.DrawModeEnabled, () => this.onInit());
    this.controller.mapInstance.once(MapEvent.InteractionModeEnabled, () => this.onEnabled());
    this.controller.mapInstance.once(MapEvent.InteractionModeDisabled, () => this.onDestroy());
  }

  static getInteractionDefinition(identifier: string): InteractionDefinition | undefined {
    return this.interactions[identifier];
  }

  addControl(control: VueComponentControl, position) {
    this.controller.mapInstance.addControl(control, position);
    this._controls.push(control);
  }

  removeControls() {
    this._controls.forEach(control => {
      this.controller.mapInstance.removeControl(control)
    });
    this._controls = [];
  }

  public getControls(): VueComponentControl[] {
    return this._controls;
  }

  public hideControls(): void {
    this._controls.forEach((x: VueComponentControl) => {
      x.toggle(false);
    })
  }

  public showControls(): void {
    this._controls.forEach((x: VueComponentControl) => {
      x.toggle(true);
    })
  }

  updateCurrentInteraction(interactionIdentifier: string, instance: DrawCustomModeThis & DrawCustomMode, state: InteractionState): void {
    this.currentInteraction = this._interactionMap[interactionIdentifier];
    this.currentInteractionIdentifier = interactionIdentifier;
    this.currentInteractionState = state;

    this.currentInteractionProxy = instance;
    this.currentInteractionAPI = (instance as any)._ctx;
  }

  getInteractions(): {[key: string]: InteractionDefinition} {
    return (<typeof AbstractInteractionMode>this.constructor).interactions
  }

  onInit(): void {
    if (!this._customButtonSubscriber) {
      this._customButtonSubscriber = (event: {action, id}) => {
        switch (event.action?.actionType) {
          case 'open_form_in_modal':
            this.controller.configuration.dialogProvider().open(FormRenderPageModalContainer, {
              props: {
                modal: true,
                maximizable: true,
                /*style: {
                  width: "1024px",
                },*/
              },
              data: {
                isOpenInModal: true,
                formTypeId: event.action?.document?.documentType?.documentTypeId,
              },
              onClose: (options) => {
                this.controller?.mapInstance?.fire(MapEvent.ToggleCustomMapButton, {...(event ?? {}),active:false})
              }
            }
            );
            break;
          case 'open_form_in_page':
            break;
          case 'map_interaction':
            let visibility;
            if (this.controller?.interaction?.activeCustomControl === event.id) {
              this.controller.interaction.activeCustomControl = null;
              this.controller.interaction.searchErrorMessage = null;
              if (this.controller?.configuration?.mapLayers?.length) {
                this.controller.getLayers().forEach(layer => {
                  visibility = this.controller?.configuration?.mapLayers?.find(x => x.documentId === layer.documentId)?.visibility;
                  this.controller.setLayerVisibility(layer, visibility);
                })
              }

              this.controller.mapInstance.fire(MapEvent.ToggleMapInfoButton, {toggled: this.controller?.configuration?.mapAddOns?.find(x => x === 'info') != null});

              this.controller.mapInstance.fire(MapEvent.ToggleCustomMapButton, {active: false, id: event.id});

              this.controller.interaction.setMode(this.controller.interaction.getMode(event.action.mapMode));
            }
            else {
              this.activateCustomControl(event)
            }
            this.controller.mapInstance.fire(MapEvent.Tick);
            this.controller.mapInstance.fire(MapEvent.VisibleLayerListChanged);
            break;
          case 'post_message':
            if(typeof event?.action?.message === 'string') event.action.message = JSON.parse(event.action.message)
              this.controller.mapInstance.fire(MapEvent.PostMessage, event.action.message);
            break;
        }
      };
      this._mapInstance.on(MapEvent.CustomButtonActioned, this._customButtonSubscriber);

    }
  }

  activateCustomControl(event) {
    let visibility, layerCenterAndZoom;
    let centerAndZoom;
    this.controller.interaction.activeCustomControl = event.id;
    this.controller.interaction.searchErrorMessage = event.action?.settings?.searchErrorMessage;
    if (event.action?.settings?.visibleLayers?.length) {
      this.controller.getLayers().forEach(layer => {
        layerCenterAndZoom = this.controller.getCenterAndZoom(layer)
        visibility = event.action?.settings?.visibleLayers?.includes(layer.documentId)
        if (visibility && (event?.action?.layer?.documentId === layer?.documentId || (!event?.action?.layer?.documentId && event.action?.settings?.visibleLayers?.length && layer.documentId === event.action?.settings?.visibleLayers?.[0]) ))
          if (!centerAndZoom) centerAndZoom = layerCenterAndZoom;
          else if (centerAndZoom?.zoom > layerCenterAndZoom?.zoom) centerAndZoom = layerCenterAndZoom
        this.controller.setLayerVisibility(layer, visibility);
      })
    }
    else if (this.controller?.configuration?.mapLayers?.length) {
      this.controller.getLayers().forEach(layer => {
        layerCenterAndZoom = this.controller.getCenterAndZoom(layer)
        visibility = this.controller?.configuration?.mapLayers?.find(x => x.documentId === layer.documentId)?.visibility;
        if (visibility && (event?.action?.layer?.documentId === layer?.documentId  || (!event?.action?.layer?.documentId && event.action?.settings?.visibleLayers?.length && layer.documentId === event.action?.settings?.visibleLayers?.[0]) ))
          if (!centerAndZoom) centerAndZoom = layerCenterAndZoom;
          else if (centerAndZoom?.zoom > layerCenterAndZoom?.zoom) centerAndZoom = layerCenterAndZoom
        this.controller.setLayerVisibility(layer, visibility);
      })
    }


    if (event.action?.settings?.infoButtonActive) {
      this.controller.mapInstance.fire(MapEvent.ToggleMapInfoButton, {toggled: true});
    }
    else this.controller.mapInstance.fire(MapEvent.ToggleMapInfoButton, {toggled: this.controller?.configuration?.mapAddOns?.find(x => x === 'info') != null});

    this.controller.mapInstance.fire(MapEvent.ToggleCustomMapButton, {active: true, id: event.id});

    if (centerAndZoom)
      if (centerAndZoom.bounds) this.controller.mapInstance.fitBounds(centerAndZoom.bounds, {duration: event.action?.settings?.animationDuration || 1000});
      else if (centerAndZoom.center && centerAndZoom.zoom) this.controller.mapInstance.flyTo({center: centerAndZoom.center, zoom: centerAndZoom.zoom, duration: event.action?.settings?.animationDuration || 1000});
      else if (centerAndZoom.center) this.controller.mapInstance.flyTo({center: centerAndZoom.center, duration: event.action?.settings?.animationDuration || 1000});
    this.controller.interaction.setMode(this.controller.interaction.getMode(event.action.mapMode), this.controller.getLayer(event.action.layer.documentId));
  }

  onEnabled(): void {
  }

  onDestroy(): void {
    this._mapInstance.off(MapEvent.CustomButtonActioned, this._customButtonSubscriber);
    if (this.controller.interaction.hasManualInputDialog()) {
      this.controller.interaction.hideManualInputDialog();
    }
  }

  async onLayerGeometriesCreate(features: Feature[]) {
    console.error('TODO: create features', features);
  }

  async onLayerGeometriesUpdate(layerGeometry: LayerGeometry, feature: Feature) {
    console.error('TODO: update feature geometry', feature);
  }

  async getLayerGeometries(geometries: any[]): Promise<LayerGeometry[]> {
    if (geometries?.length && this.controller?.configuration?.fetchFeatureOptions &&
      (this.controller?.configuration?.fetchFeatureOptions?.relatedDocumentIds?.length
        ||
        this.controller?.configuration?.fetchFeatureOptions?.relatedDocumentTypeIds?.length)
    )
      return await GeometryProvider.fetchIntersectedGeometries(geometries?.map(x => x.geometryId), this.controller.getLayerMap(), {
        ...this.controller?.configuration?.fetchFeatureOptions,
        "x-access-token": this.controller.configuration.accessToken
      });
    else
      return await GeometryProvider.fetchGeometries(geometries, this.controller.getLayerMap(), this.controller.configuration.accessToken);
  }

  async getGeometriesByLayer(layer: Layer): Promise<LayerGeometry[]> {
    return await GeometryProvider.fetchLayerGeometries(layer, "", this.controller.configuration.accessToken);
  }

  isAllowingChanges(): boolean {
    return (<typeof AbstractInteractionMode>this.constructor).allowChanges === true;
  }

  isAllowingPriorityContextMenu(): boolean {
    return (<typeof AbstractInteractionMode>this.constructor).allowPriorityContextMenu === true;
  }

  getIdentifier(): string {
    return (<typeof AbstractInteractionMode>this.constructor).identifier;
  }

  getDefaultInteraction(): string {
    return (<typeof AbstractInteractionMode>this.constructor).defaultInteraction;
  }

  isActionBarShowingLabels(): boolean {
    return (<typeof AbstractInteractionMode>this.constructor).actionBarShowLabels;
  }

  isShowingActionBar(): boolean {
    return (<typeof AbstractInteractionMode>this.constructor).showActionBar;
  }

  getActionBarSize() {
    return (<typeof AbstractInteractionMode>this.constructor).actionBarSize;
  }

  getLayer(): Layer | undefined {
    return this.layer;
  }

  setLayer(layer: Layer): void {
    this.layer = layer;
  }

  getGlobalActions(interactionIdentifier: string, point: mapboxgl.Point | undefined = undefined): MenuItem[] {
    return [];
  }

  getSingleActions(feature, interactionIdentifier, point: mapboxgl.Point | undefined = undefined) {
    return [];
  }

  getActionOptions(interactionIdentifier): MenuItem[] {
    //return [];
    let options: any = [];
    if (this.hasAccessTo('vertexList', 'map') && ["draw_polygon", "draw_point", "draw_line", "draw_linestring", "draw_parallelogram"].includes(interactionIdentifier))
      options.push(
        {
          label: "Vertex list",
          icon: "/icons/toolbox/list.svg",
          command: () => {
            if (this.controller.interaction.hasManualInputDialog()) {
              this.controller.interaction.hideManualInputDialog();
            } else {
              this.controller.interaction.showManualInputDialog();
            }
            this._mapInstance.fire(MapEvent.Tick);
          },
          active: this.controller.interaction.hasManualInputDialog(),
          pos: {position: 'bottom'}
        });

    /*if (this.hasAccessTo('dropImport', 'map') && ["draw_polygon", "draw_point", "draw_line", "draw_linestring"].includes(interactionIdentifier))
      options.push(
        {
          label: "Drop import",
          icon: "/icons/toolbox/drag-drop.svg",
          command: () => {
            if (this.controller.interaction.hasDropImportEnabled()) {
              this.controller.interaction.disableDropImportEnabled();
            } else {
              this.controller.interaction.enableDropImportEnabled();
            }
            this._mapInstance.fire(MapEvent.Tick);
          },
          active: this.controller.interaction.hasDropImportEnabled()
        });*/

    if (this.hasAccessTo('measuredValues', 'map') && ["draw_polygon", "draw_parallelogram"].includes(interactionIdentifier))
      options.push(
        {
          label: "Vertex length",
          icon: "/icons/toolbox/vertex-distance.svg",
          command: () => {
            if (this.controller.interaction.hasVertexLength()) {
              this.controller.interaction.hideVertexLength();
            } else {
              this.controller.interaction.showVertexLength();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasVertexLength(),
          pos: {position: 'bottom'}
        },
        {
          label: "Area",
          icon: "/icons/toolbox/polygon-area.svg",
          command: () => {
            if (this.controller.interaction.hasArea()) {
              this.controller.interaction.hideArea();
            } else {
              this.controller.interaction.showArea();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasArea(),
          pos: {position: 'bottom'}
        },
        {
          label: "Total length",
          icon: "/icons/toolbox/sum-sign.svg",
          command: () => {
            if (this.controller.interaction.hasLength()) {
              this.controller.interaction.hideLength();
            } else {
              this.controller.interaction.showLength();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasLength(),
          pos: {position: 'bottom'}
        },
        {
          label: "Angle",
          icon: "/icons/toolbox/protractor.svg",
          command: () => {
            if (this.controller.interaction.hasAngle()) {
              this.controller.interaction.hideAngle();
            } else {
              this.controller.interaction.showAngle();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasAngle(),
          pos: {position: 'bottom'}
        });
    if (this.hasAccessTo('measuredValues', 'map') && ["draw_line", "draw_linestring"].includes(interactionIdentifier))
      options.push(
        {
          label: "Vertex length",
          icon: "/icons/toolbox/vertex-distance.svg",
          command: () => {
            if (this.controller.interaction.hasVertexLength()) {
              this.controller.interaction.hideVertexLength();
            } else {
              this.controller.interaction.showVertexLength();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasVertexLength(),
          pos: {position: 'bottom'}
        },
        {
          label: "Total length",
          icon: "/icons/toolbox/sum-sign.svg",
          command: () => {
            if (this.controller.interaction.hasLength()) {
              this.controller.interaction.hideLength();
            } else {
              this.controller.interaction.showLength();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasLength(),
          pos: {position: 'bottom'}
        },
        {
          label: "Angle",
          icon: "/icons/toolbox/protractor.svg",
          command: () => {
            if (this.controller.interaction.hasAngle()) {
              this.controller.interaction.hideAngle();
            } else {
              this.controller.interaction.showAngle();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasAngle(),
          pos: {position: 'bottom'}
        });

    if (this.hasAccessTo('measuredValues', 'map') && ["select"].includes(interactionIdentifier))
      options.push(
        {
          label: "Vertex length",
          icon: "/icons/toolbox/vertex-distance.svg",
          command: () => {
            if (this.controller.interaction.hasVertexLength()) {
              this.controller.interaction.hideVertexLength();
            } else {
              this.controller.interaction.showVertexLength();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasVertexLength(),
          pos: {position: 'bottom'}
        },
        {
          label: "Area",
          icon: "/icons/toolbox/polygon-area.svg",
          command: () => {
            if (this.controller.interaction.hasArea()) {
              this.controller.interaction.hideArea();
            } else {
              this.controller.interaction.showArea();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasArea(),
          pos: {position: 'bottom'}
        },
        {
          label: "Total length",
          icon: "/icons/toolbox/sum-sign.svg",
          command: () => {
            if (this.controller.interaction.hasLength()) {
              this.controller.interaction.hideLength();
            } else {
              this.controller.interaction.showLength();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasLength(),
          pos: {position: 'bottom'}
        },
        {
          label: "Angle",
          icon: "/icons/toolbox/protractor.svg",
          command: () => {
            if (this.controller.interaction.hasAngle()) {
              this.controller.interaction.hideAngle();
            } else {
              this.controller.interaction.showAngle();
            }
            this.controller.interaction.mode.changeInteraction(this.controller.interaction.mode.currentInteractionIdentifier);

          },
          active: this.controller.interaction.hasAngle(),
          pos: {position: 'bottom'}
        });

    return options;
  }


  getMultiActions(features, interactionIdentifier, point: mapboxgl.Point | undefined = undefined) {
    return [];
  }

  getInteractionModeActions(interactionIdentifier: string, point: mapboxgl.Point | undefined = undefined): MenuItem[] {
    return [];
  }

  onAdd(map: mapboxgl.Map): HTMLElement {
    this._mapInstance = map;

    let drawModeProxies = {};
    Object.keys(this.getInteractions()).forEach(interactionIdentifier => {
      let definition = this.getInteractions()[interactionIdentifier];
      let proxyConfiguration = InteractionProxy(interactionIdentifier, definition, this, this.controller);
      drawModeProxies[interactionIdentifier] = proxyConfiguration.proxy;
      this._interactionMap[interactionIdentifier] = proxyConfiguration.instance;
    });

    //if(!this.defaultInteraction && this.controller?.interaction?.mode?.getIdentifier() === 'drawer' && this.controller?.interaction?.configuration?.interaction) this.defaultInteraction = this.controller?.interaction?.configuration?.interaction
    let dstyles = (<typeof AbstractInteractionMode>this.constructor).drawStyles
    this._drawControl = new MapboxDraw({
      boxSelect: true,
      displayControlsDefault: false,
      styles: this.drawStyles ?? dstyles,
      modes: drawModeProxies,
      defaultMode: this.defaultInteraction ?? (<typeof AbstractInteractionMode>this.constructor).defaultInteraction
    });
    return this._drawControl.onAdd(map);
  }

  /*updateDrawStyles(newStyles) {
    if (this._drawControl) {
      // Remove the current draw control
      this.controller.mapInstance.removeControl(this._drawControl);
    }
    let drawModeProxies = {};
    Object.keys(this.getInteractions()).forEach(interactionIdentifier => {
      let definition = this.getInteractions()[interactionIdentifier];
      let proxyConfiguration = InteractionProxy(interactionIdentifier, definition, this, this.controller);
      drawModeProxies[interactionIdentifier] = proxyConfiguration.proxy;
      this._interactionMap[interactionIdentifier] = proxyConfiguration.instance;
    });

    // Create a new draw control with updated styles
    this._drawControl = new MapboxDraw({
      boxSelect: true,
      displayControlsDefault: false,
      styles: newStyles,
      modes: drawModeProxies,
      defaultMode: this.defaultInteraction ?? (<typeof AbstractInteractionMode>this.constructor).defaultInteraction
    });

    // Add the updated draw control to the map
    this.controller.mapInstance.addControl(this._drawControl);
  }*/


  onRemove(map: mapboxgl.Map) {
    return this._drawControl.onRemove(map);
  }

  add(feature) {
    return this._drawControl.add(feature);
  }

  get(featureId) {
    return this._drawControl.get(featureId);
  }

  updateCursor(): void {
    this._drawControl.getAll().features.forEach(feature => {
      if (feature.id === 'aim') {
        feature.properties.icon = this.controller.configuration.cursor;
        this._drawControl.add(feature);
      }
    })
  }

  delete(featureId) {
    return this._drawControl.delete([featureId]);
  }

  reset() {
    // this.controller.interaction.getDrawMode().reset(this.controller.interaction.getDrawState());
  }

  changeInteraction(interactionIdentifier: string, state: InteractionState | undefined = undefined): void {
    let nextDefinition = this.getInteractions()[interactionIdentifier] ?? undefined;

    if (!nextDefinition) {
      throw new Error(`Invalid map interaction ${interactionIdentifier}`)
    }

    if (state) {
      this._drawControl.changeMode(interactionIdentifier, state);
      return;
    }

    if (nextDefinition.options.changeset !== this.currentInteraction.options.changeset && this.controller.interaction.hasHistory()) {
      if (!confirm(this.controller.configuration.translatorProvider('Are you sure you want to discard these changes?'))) {
        return;
      }
      this.controller.interaction.historyReset();
    }

    if (this.currentInteractionState.isDirty) {
      this.changeInteraction(interactionIdentifier, this.currentInteractionState);
      return;
    }
    if (!nextDefinition.options.selectionAllowed && !nextDefinition.options.selectionRequired) {
      this.controller.interaction.setFocus([]);
    }
    let options = new InteractionState(this.controller.interaction.getFocused().map(layerGeometry => layerGeometry.geometry));

    this._drawControl.changeMode(interactionIdentifier, options);


    if (this.controller.interaction.hasManualInputDialog() && !this.isManualInputSupported()) {
      this.controller.interaction.hideManualInputDialog();
    }

    this._mapInstance.fire(MapEvent.InteractionChanged, {interaction: interactionIdentifier});

    //this.controller.interaction.hideVertexLength();
    //this.controller.interaction.hideArea();
    //this.controller.interaction.hideLength();
  }

  resetInteraction(): void {
    this.changeInteraction(this.currentInteractionIdentifier);
  }

  isManualInputSupported(): boolean {
    return this.currentInteraction.isManualInputSupported(this.currentInteractionState);
  }

  async setCoordinates(points: Position[]): Promise<void> {
    await this.currentInteraction.setCoordinates(this.currentInteractionState, points);
  }

  getCoordinates(): GeometryCoordinate[] {
    return this.currentInteraction.getCoordinates(this.currentInteractionState);
  }

  canSave(): boolean {
    return this.currentInteraction.canSave(this.currentInteractionState);
  }

  showHistory(): boolean {
    return this.currentInteraction.showHistory(this.currentInteractionState)
  }

  async save(): Promise<void> {
    await this.currentInteraction.save(this.currentInteractionState);
  }

  changeDrawMode(modeId, opts = undefined) {
    let currentActiveDrawMode = this.getDrawModeIdentifier();
    this._drawControl.trash();
    this._drawControl.changeMode(modeId, {geometries: this.controller.interaction.getFocused(), ...opts, });

    if (modeId !== currentActiveDrawMode) {
      this.reset();
    }
  }

  getDrawModeIdentifier(): string {
    return this._drawControl.getMode();
  }

  hasAccessTo(right = null, module = null, options = {}): boolean {

    let defaultOptions = {
      documentCategory: null,
      documentType: null,
      documentId: null,
      documentIds: null,
      organizationIds: null,
      parentDocumentId: null,
      isPrivate: false
    }

    options = {
      ...defaultOptions,
      ...options
    }

    return hasAccessTo(
      this.controller?.configuration?.user?.rights || [],
      getRightId(right, this.controller?.configuration?.commonData?.rightsList || []),
      {
        moduleId: getModuleId(module, this.controller?.configuration?.commonData?.modules || []),
        documentCategoryId: getDocumentCategoryId(options.documentCategory, this.controller?.configuration?.commonData?.documentCategories || []),
        documentTypeId: getDocumentTypeId(options.documentType, this.controller?.configuration?.commonData?.documentTypes || []),
        documentId: (options.documentId || null),
        organizationIds: options.organizationIds,
        parentDocumentId: options.parentDocumentId,
        isPrivate: options.isPrivate
      }
    );
  }
}

export class DrawModeOption {
  constructor(
    public identifier: string,
    public interaction: typeof AbstractDrawMode,
    public requireSelection: boolean = false,
    public multiSelection: boolean = false,
  ) {
  }
}

export class DrawStyleUtil {
  static drawStyle(
    opts: Partial<{
      "line-color": string | any,
      "line-dasharray": object,
      "fill-color": string | any,
      "fill-opacity": number,
      "border-color": string,
      "line-width": number | undefined,
    }> = {}): any[] {
    opts = {
      "line-color": '#026ab4',
      "fill-color": '#ECA0C0',
      "fill-opacity": 0.8,
      "border-color": '#026ab4',
      ...opts
    }

    return [
      ...this.drawLineStyle(opts),
      ...this.drawPolygonStyle(opts),
      ...this.drawPointStyle(opts),
      ...this.drawLabelStyle(),
      ...this.drawIconStyle()
    ]
  }

  static drawLineStyle(opts): any[] {
    if (opts.hasOwnProperty('line-dasharray') && !opts['line-dasharray'].length)
      delete opts['line-dasharray'];

    return [
      {
        id: "gl-draw-line",
        type: "line",
        filter: ["all", ["==", "$type", "LineString"], ["!=", "mode", "static"], ["!=", "focusStyle", true]],
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          ...getFilteredObjectByKeyPrefix(opts, 'line', 'paint'),
        },
      },
      {
        id: "gl-draw-line-static",
        type: "line",
        filter: ["all", ["==", "$type", "LineString"], ["==", "mode", "static"], ["!=", "focusStyle", true]],
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#000",
          "line-width": opts['line-width'] || 4,
        },
      }
    ];
  }

  static drawPolygonStyle(opts): any[] {
    return [
      {
        id: "gl-draw-polygon-fill",
        type: "fill",
        filter: [
          "all",
          ["==", "$type", "Polygon"],
          ["!=", "mode", "static"],
          ["!=", "selection", "outline"],
          ["!=", "focusStyle", true]
        ],
        paint: {
          ...getFilteredObjectByKeyPrefix(opts, 'fill', 'paint'),
        },
      },
      {
        id: "gl-draw-polygon-midpoint",
        type: "circle",
        filter: [
          "all",
          ["==", "$type", "Point"],
          ["==", "meta", "midpoint"],
          ["!=", "focusStyle", true]
        ],
        paint: {
          "circle-radius": 5,
          "circle-color": "#fbb03b",
        },
      },
      {
        id: "gl-draw-polygon-stroke-active",
        type: "line",
        filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"],
          ["!=", "focusStyle", true]],
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          ...getFilteredObjectByKeyPrefix(opts, 'line', 'paint'),
        },
      },
      {
        id: "gl-draw-polygon-and-line-vertex-halo-active",
        type: "circle",
        filter: [
          "all",
          ["==", "meta", "vertex"],
          ["==", "$type", "Point"],
          ["!=", "mode", "static"],
          ["!=", "focusStyle", true]
        ],
        paint: {
          "circle-radius": 7,
          "circle-color": "#FFF",
        },
      },
      {
        id: "gl-draw-polygon-and-line-vertex-active",
        type: "circle",
        filter: [
          "all",
          ["==", "meta", "vertex"],
          ["==", "$type", "Point"],
          ["!=", "mode", "static"],
          ["!=", "focusStyle", true]
        ],
        paint: {
          "circle-radius": 5,
          "circle-color": "#D20C0C",
        },
      },
      {
        id: "gl-draw-polygon-fill-static",
        type: "fill",
        filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"],
          ["!=", "focusStyle", true]],
        paint: {
          "fill-color": "#000",
          "fill-outline-color": "#000",
          "fill-opacity": 0.1,
        },
      },
      {
        id: "gl-draw-polygon-stroke-static",
        type: "line",
        filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"],
          ["!=", "focusStyle", true]],
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#000",
          "line-width": 4,
        },
      }
    ]
  }

  static drawPointStyle(opts): any[] {
    return [
      {
        id: "gl-draw-point-point-stroke-inactive",
        type: "circle",
        filter: [
          "all",
          ["==", "active", "false"],
          ["==", "$type", "Point"],
          ["==", "meta", "feature"],
          ["!=", "mode", "static"],
          ["!=", "focusStyle", true]
        ],
        paint: {
          "circle-radius": 5,
          "circle-opacity": 1,
          "circle-color": "black",
          ...getFilteredObjectByKeyPrefix(opts, 'circle', 'paint'),
          ...getFilteredObjectByKeyPrefix(opts, 'icon', 'paint'),
        },
      },
      {
        id: "gl-draw-point-inactive",
        type: "circle",
        filter: [
          "all",
          ["==", "active", "false"],
          ["==", "$type", "Point"],
          ["==", "meta", "feature"],
          ["!=", "mode", "static"],
          ["!=", "focusStyle", true]
        ],
        paint: {
          "circle-radius": 3,
          "circle-color": "green",
          ...getFilteredObjectByKeyPrefix(opts, 'circle', 'paint'),
          ...getFilteredObjectByKeyPrefix(opts, 'icon', 'paint'),
        },
      },
      {
        id: "gl-draw-point-stroke-active",
        type: "circle",
        filter: [
          "all",
          ["==", "$type", "Point"],
          ["==", "active", "true"],
          ["!=", "meta", "midpoint"],
          ["!=", "focusStyle", true]
        ],
        paint: {
          "circle-radius": 7,
          "circle-color": "blue",
          ...getFilteredObjectByKeyPrefix(opts, 'circle', 'paint'),
          ...getFilteredObjectByKeyPrefix(opts, 'icon', 'paint'),
        },
      },
      {
        id: "gl-draw-point-active",
        type: "circle",
        filter: [
          "all",
          ["==", "$type", "Point"],
          ["!=", "meta", "midpoint"],
          ["==", "active", "true"],
          ["!=", "focusStyle", true]
        ],
        paint: {
          "circle-radius": 7,
          "circle-color": "#E6A2BC",
        },
      }
    ]
  }

  static drawLabelStyle(opts: Partial<{paint: any, layout: any}> = {}): any[] {
    return [{
      id: "gl-draw-label",
      type: "symbol",
      filter: [
        "all",
        ["==", "$type", "Point"],
        ["==", "meta", "label"],
        ["!=", "focusStyle", true]
      ],
      layout: {
        "text-field": ["get", "label"],
        "text-allow-overlap": true,
        ...(opts.layout ?? {})
      },
      paint: {
        "text-color": "#ffffff",
        "text-halo-width": 2,
        "text-halo-blur": 2,
        "text-halo-color": "rgb(20,56,6)",
        ...(opts.paint ?? {})
      }
    }]
  }

  static drawIconStyle(opts: Partial<{paint: any, layout: any}> = {}): any[] {
    return [
      {
        id: "gl-draw-icon-3d-center",
        type: "symbol",
        filter: [
          "all",
          ["==", "$type", "Point"],
          ["==", "meta", "icon-3d-center"],
          ["!=", "focusStyle", true]
        ],
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": ["number", ["get", "icon-size"], 1],
          "icon-allow-overlap": true,
          ...(opts.layout ?? {})
        },
        paint: {
          "icon-opacity": 1,
          "icon-color": "#ffffff",
          "icon-halo-color": "#000000",
          "icon-halo-width": 1,
        }
      },
      {
        id: "gl-draw-icon-icon-flat-center",
        type: "symbol",
        filter: [
          "all",
          ["==", "$type", "Point"],
          ["==", "meta", "icon-flat-center"],
          ["!=", "focusStyle", true]
        ],
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": ["number", ["get", "icon-size"], 1],
          "icon-pitch-alignment": "map",
          "icon-allow-overlap": true,
          ...(opts.layout ?? {})
        },
        paint: {
          "icon-opacity": 1,
          "icon-color": "#ffffff",
          "icon-halo-color": "#000000",
          "icon-halo-width": 1,
        }
      },
      {
        id: "gl-draw-icon-icon-3d-bottom",
        type: "symbol",
        filter: [
          "all",
          ["==", "$type", "Point"],
          ["==", "meta", "icon-3d-bottom"],
          ["!=", "focusStyle", true]
        ],
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": ["number", ["get", "icon-size"], 1],
          "icon-allow-overlap": true,
          "icon-anchor": "bottom",
          ...(opts.layout ?? {})
        },
        paint: {
          "icon-opacity": 1,
          "icon-color": "#ffffff",
          "icon-halo-color": "#000000",
          "icon-halo-width": 1,
        }
      },
    ];
  }
}
